import { ChangeEvent, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { resolveAssetUrl, uploadAdminAsset } from "../utils/assets";
import {
  getXpRules,
  getAchievements,
  saveXpRules,
  updateAchievement,
  deleteAchievement,
  createAchievement,
  bootstrapDefaultAchievements,
  type XpRules,
  type AchievementItem,
} from "../api/adminGamificationApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";

const defaultRules: XpRules = {
  expPerRead: 1,
  expPerComment: 2,
  expPerPurchase: 5,
  dailyCommentLimit: 3,
};

const emptyAchievement: Omit<AchievementItem, "id"> = {
  title: "",
  description: "",
  iconUrl: "",
  conditionType: "read_count",
  conditionValue: 10,
  nameColor: "",
  avatarFrame: "",
};

const IMAGE_UPLOAD_HINT = "Format: PNG/JPG/JPEG/WEBP/SVG/GIF/AVIF • Maksimum: 10 MB";

const conditionOptions = [
  { value: "read_count", label: "Okuma Sayısı" },
  { value: "comment_count", label: "Yorum Sayısı" },
  { value: "purchase_count", label: "Satın Alma" },
  { value: "feedback_count", label: "Feedback" },
  { value: "unique_content_count", label: "Farklı Seri" },
  { value: "favorite_count", label: "Favori Sayısı" },
  { value: "consecutive_read_days", label: "Ardışık Okuma Günleri" },
  { value: "helpful_feedback_count", label: "Yararlı Feedback" },
  { value: "creator_like_count", label: "Creator Beğenileri" },
  { value: "creator_read_count", label: "Creator Okunma" },
];

export default function AdminGamification() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("Gamification.View");
  const [rules, setRules] = useState<XpRules>(defaultRules);
  const [rulesEdit, setRulesEdit] = useState<XpRules>(defaultRules);
  const [achievements, setAchievements] = useState<AchievementItem[]>([]);
  const [newAchievement, setNewAchievement] = useState<Omit<AchievementItem, "id">>(emptyAchievement);
  const [isBootstrapping, setIsBootstrapping] = useState(false);

  const resolveIconUrl = (raw?: string | null) => resolveAssetUrl(raw);
  const getUploadErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }
    return "Gorsel yuklenemedi. Lutfen tekrar deneyin.";
  };

  const uploadAchievementIcon = async (file: File): Promise<string | null> => {
    try {
      const fileName = await uploadAdminAsset("/admin/gamification/achievements/upload-icon", file);
      if (fileName) {
        Swal.fire("Yüklendi", "Rozet görseli yüklendi.", "success");
        return fileName;
      }
      Swal.fire("Hata", "Rozet görseli yüklenemedi.", "error");
    } catch (err) {
      console.error("achievement icon upload failed", err);
      Swal.fire("Hata", getUploadErrorMessage(err), "error");
    }

    return null;
  };

  const uploadAvatarFrameImage = async (file: File): Promise<string | null> => {
    try {
      const fileName = await uploadAdminAsset("/admin/gamification/achievements/upload-frame", file);
      if (fileName) {
        Swal.fire("Yüklendi", "Avatar çerçevesi yüklendi.", "success");
        return fileName;
      }
      Swal.fire("Hata", "Avatar çerçevesi yüklenemedi.", "error");
    } catch (err) {
      console.error("avatar frame upload failed", err);
      Swal.fire("Hata", getUploadErrorMessage(err), "error");
    }
    return null;
  };

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const loadData = async () => {
      const [xp, ach] = await Promise.all([getXpRules(), getAchievements()]);
      const mergedRules = { ...defaultRules, ...xp };
      setRules(mergedRules);
      setRulesEdit(mergedRules);
      setAchievements(ach);
    };

    loadData();
  }, [allowed]);

  if (checkingPermission) {
    return <div className="p-8 text-center text-sm text-gray-500">Yetki kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="p-8 text-center text-sm text-red-500">Bu sayfaya erişim yetkin yok.</div>;
  }

  const handleSaveRules = async () => {
    const response = await saveXpRules(rulesEdit);
    if (!response.success) {
      Swal.fire("Hata", "XP kuralları kaydedilemedi.", "error");
      return;
    }

    setRules(rulesEdit);
    Swal.fire("Kaydedildi", "XP kuralları güncellendi.", "success");
  };

  const handleExistingAchievementIconChange = async (
    achievementId: string,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const uploaded = await uploadAchievementIcon(file);
    if (uploaded) {
      setAchievements((prev) =>
        prev.map((item) => (item.id === achievementId ? { ...item, iconUrl: uploaded } : item)),
      );
    }

    event.target.value = "";
  };

  const clearExistingAchievementIcon = (achievementId: string) => {
    setAchievements((prev) => prev.map((item) => (item.id === achievementId ? { ...item, iconUrl: "" } : item)));
  };

  const handleExistingAchievementFrameChange = async (
    achievementId: string,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const uploaded = await uploadAvatarFrameImage(file);
    if (uploaded) {
      setAchievements((prev) =>
        prev.map((item) => (item.id === achievementId ? { ...item, avatarFrame: uploaded } : item)),
      );
    }

    event.target.value = "";
  };

  const clearExistingAchievementFrame = (achievementId: string) => {
    setAchievements((prev) => prev.map((item) => (item.id === achievementId ? { ...item, avatarFrame: "" } : item)));
  };

  const handleNewAchievementIconChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const uploaded = await uploadAchievementIcon(file);
    if (uploaded) {
      setNewAchievement((prev) => ({ ...prev, iconUrl: uploaded }));
    }

    event.target.value = "";
  };

  const clearNewAchievementIcon = () => {
    setNewAchievement((prev) => ({ ...prev, iconUrl: "" }));
  };

  const handleNewAchievementFrameChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const uploaded = await uploadAvatarFrameImage(file);
    if (uploaded) {
      setNewAchievement((prev) => ({ ...prev, avatarFrame: uploaded }));
    }

    event.target.value = "";
  };

  const clearNewAchievementFrame = () => {
    setNewAchievement((prev) => ({ ...prev, avatarFrame: "" }));
  };

  const handleUpdateAchievement = async (achievement: AchievementItem) => {
    const response = await updateAchievement(achievement);
    if (!response.success) {
      Swal.fire("Hata", "Başarım güncellenemedi.", "error");
      return;
    }

    Swal.fire("Kaydedildi", "Başarım güncellendi.", "success");
  };

  const handleDeleteAchievement = async (id: string) => {
    const confirm = await Swal.fire({
      title: "Bu başarım silinsin mi?",
      text: "Bu işlem geri alınamaz.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sil",
      cancelButtonText: "Vazgeç",
      confirmButtonColor: "#dc2626",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    const response = await deleteAchievement(id);
    if (!response.success) {
      Swal.fire("Hata", "Başarım silinemedi.", "error");
      return;
    }

    setAchievements((prev) => prev.filter((item) => item.id !== id));
    Swal.fire("Silindi", "Başarım kaldırıldı.", "success");
  };

  const handleCreateAchievement = async () => {
    if (!newAchievement.title.trim()) {
      Swal.fire("Uyarı", "Başlık zorunludur.", "warning");
      return;
    }

    const response = await createAchievement(newAchievement);
    if (!response.success) {
      Swal.fire("Hata", "Başarım oluşturulamadı.", "error");
      return;
    }

    const fresh = await getAchievements();
    setAchievements(fresh);
    setNewAchievement(emptyAchievement);
    Swal.fire("Oluşturuldu", "Başarım eklendi.", "success");
  };

  const handleBootstrapDefaults = async () => {
    const decision = await Swal.fire({
      title: "Varsayılan başarımlar yüklensin mi?",
      text: "İstersen sadece eksikleri ekleyebilir veya mevcutları üzerine yazabilirsin.",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Sadece eksikleri ekle",
      denyButtonText: "Üzerine yazarak güncelle",
      cancelButtonText: "Vazgeç",
    });

    if (decision.isDismissed) {
      return;
    }

    setIsBootstrapping(true);
    try {
      const overwriteExisting = decision.isDenied;
      const result = await bootstrapDefaultAchievements(overwriteExisting);
      if (!result.success) {
        Swal.fire("Hata", "Varsayılan başarımlar yüklenemedi.", "error");
        return;
      }

      const fresh = await getAchievements();
      setAchievements(fresh);
      Swal.fire(
        "Tamamlandı",
        `Oluşturulan: ${result.created} • Güncellenen: ${result.updated} • Kazandırılan: ${result.awarded}`,
        "success",
      );
    } finally {
      setIsBootstrapping(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Gamification Kontrolleri</h1>

      <section className="rounded bg-white p-5 shadow">
        <h2 className="text-lg font-semibold text-gray-800">XP Kuralları</h2>
        <p className="text-xs text-gray-500">Adjust the XP awarded for key user actions. The leveling formula remains server-defined.</p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm text-gray-600">
            <div className="mb-1">XP per read</div>
            <input
              type="number"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={rulesEdit.expPerRead}
              min={0}
              onChange={(e) => setRulesEdit({ ...rulesEdit, expPerRead: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm text-gray-600">
            <div className="mb-1">XP per comment</div>
            <input
              type="number"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={rulesEdit.expPerComment}
              min={0}
              onChange={(e) => setRulesEdit({ ...rulesEdit, expPerComment: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm text-gray-600">
            <div className="mb-1">XP per purchase</div>
            <input
              type="number"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={rulesEdit.expPerPurchase}
              min={0}
              onChange={(e) => setRulesEdit({ ...rulesEdit, expPerPurchase: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm text-gray-600">
            <div className="mb-1">Daily comment limit (0 = sınırsız)</div>
            <input
              type="number"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={rulesEdit.dailyCommentLimit}
              min={0}
              onChange={(e) => setRulesEdit({ ...rulesEdit, dailyCommentLimit: Number(e.target.value) })}
            />
          </label>
        </div>

        <div className="mt-4 flex gap-3">
          <button
            onClick={handleSaveRules}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            XP kurallarını kaydet
          </button>
          <button
            onClick={() => setRulesEdit(rules)}
            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Değişiklikleri sıfırla
          </button>
        </div>
      </section>

      <section className="space-y-4 rounded bg-white p-5 shadow">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Başarımlar</h2>
            <p className="text-xs text-gray-500">Manage badges, thresholds, and optional cosmetics unlocked by achievements.</p>
          </div>
          <button
            type="button"
            onClick={handleBootstrapDefaults}
            disabled={isBootstrapping}
            className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isBootstrapping ? "Yükleniyor..." : "Varsayılan başarımları yükle"}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Başlık</th>
                <th className="px-3 py-2">Koşul</th>
                <th className="px-3 py-2">Icon URL</th>
                <th className="px-3 py-2">Aksiyonlar</th>
              </tr>
            </thead>
            <tbody>
              {achievements.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-500">
                    Henüz başarım tanımlanmadı.
                  </td>
                </tr>
              )}
              {achievements.map((achievement, idx) => (
                <tr key={achievement.id} className="border-b last:border-none">
                  <td className="px-3 py-2 align-top">
                    <input
                      className="mb-2 w-full rounded border border-gray-300 p-2 text-sm"
                      value={achievement.title}
                      onChange={(e) =>
                        setAchievements((prev) => {
                          const copy = [...prev];
                          copy[idx] = { ...copy[idx], title: e.target.value };
                          return copy;
                        })
                      }
                    />
                    <textarea
                      className="h-20 w-full rounded border border-gray-300 p-2 text-sm"
                      value={achievement.description}
                      onChange={(e) =>
                        setAchievements((prev) => {
                          const copy = [...prev];
                          copy[idx] = { ...copy[idx], description: e.target.value };
                          return copy;
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <select
                      className="mb-2 w-full rounded border border-gray-300 p-2 text-sm"
                      value={achievement.conditionType}
                      onChange={(e) =>
                        setAchievements((prev) => {
                          const copy = [...prev];
                          copy[idx] = { ...copy[idx], conditionType: e.target.value };
                          return copy;
                        })
                      }
                    >
                      {conditionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      className="w-full rounded border border-gray-300 p-2 text-sm"
                      value={achievement.conditionValue}
                      min={1}
                      onChange={(e) =>
                        setAchievements((prev) => {
                          const copy = [...prev];
                          copy[idx] = { ...copy[idx], conditionValue: Number(e.target.value) };
                          return copy;
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        {resolveIconUrl(achievement.iconUrl) ? (
                          <img
                            src={resolveIconUrl(achievement.iconUrl)}
                            alt={achievement.title}
                            className="h-12 w-12 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-gray-300 text-[11px] text-gray-400">
                            İkon yok
                          </div>
                        )}
                        <div className="flex gap-2">
                          <label className="cursor-pointer rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700">
                            Yükle
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(event) => handleExistingAchievementIconChange(achievement.id, event)}
                            />
                          </label>
                          {achievement.iconUrl ? (
                            <button
                              type="button"
                              className="rounded bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                              onClick={() => clearExistingAchievementIcon(achievement.id)}
                            >
                              Kaldır
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <input
                        className="w-full rounded border border-gray-300 p-2 text-sm"
                        placeholder="Icon URL"
                        value={achievement.iconUrl ?? ""}
                        onChange={(e) =>
                          setAchievements((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], iconUrl: e.target.value };
                            return copy;
                          })
                        }
                      />
                      <div className="grid gap-3">
                        <input
                          className="w-full rounded border border-gray-300 p-2 text-sm"
                          placeholder="Name color (#hex)"
                          value={achievement.nameColor ?? ""}
                          onChange={(e) =>
                            setAchievements((prev) => {
                              const copy = [...prev];
                              copy[idx] = { ...copy[idx], nameColor: e.target.value };
                              return copy;
                            })
                          }
                        />
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            {resolveIconUrl(achievement.avatarFrame) ? (
                              <img
                                src={resolveIconUrl(achievement.avatarFrame)}
                                alt={`${achievement.title} frame`}
                                className="h-12 w-12 rounded-full border border-gray-200 object-cover"
                              />
                            ) : (
                              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-gray-300 text-[11px] text-gray-400">
                                Çerçeve yok
                              </div>
                            )}
                            <div className="flex gap-2">
                              <label className="cursor-pointer rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700">
                                Çerçeve Yükle
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(event) => handleExistingAchievementFrameChange(achievement.id, event)}
                                />
                              </label>
                              {achievement.avatarFrame ? (
                                <button
                                  type="button"
                                  className="rounded bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                                  onClick={() => clearExistingAchievementFrame(achievement.id)}
                                >
                                  Kaldır
                                </button>
                              ) : null}
                            </div>
                          </div>
                          <input
                            className="w-full rounded border border-gray-300 p-2 text-sm"
                            placeholder="Avatar frame URL"
                            value={achievement.avatarFrame ?? ""}
                            onChange={(e) =>
                              setAchievements((prev) => {
                                const copy = [...prev];
                                copy[idx] = { ...copy[idx], avatarFrame: e.target.value };
                                return copy;
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdateAchievement(achievements[idx])}
                        className="rounded bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => handleDeleteAchievement(achievement.id)}
                        className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded border border-dashed border-gray-300 p-4">
          <h3 className="text-sm font-semibold text-gray-700">Yeni başarım oluştur</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-6">
            <div className="space-y-3 rounded border border-dashed border-gray-300 p-3 sm:col-span-2">
              <div className="flex items-center gap-3">
                {resolveIconUrl(newAchievement.iconUrl) ? (
                  <img
                    src={resolveIconUrl(newAchievement.iconUrl)}
                    alt={newAchievement.title || "Yeni rozet"}
                    className="h-12 w-12 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-gray-300 text-[11px] text-gray-400">
                    İkon yok
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="cursor-pointer rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700">
                    Yükle
                    <input type="file" accept="image/*" className="hidden" onChange={handleNewAchievementIconChange} />
                    <span className="text-[11px] text-gray-500">{IMAGE_UPLOAD_HINT}</span>
                  </label>
                  {newAchievement.iconUrl ? (
                    <button
                      type="button"
                      className="rounded bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                      onClick={clearNewAchievementIcon}
                    >
                      Kaldır
                    </button>
                  ) : null}
                </div>
              </div>
              <input
                className="w-full rounded border border-gray-300 p-2 text-sm"
                placeholder="Icon URL"
                value={newAchievement.iconUrl ?? ""}
                onChange={(e) => setNewAchievement({ ...newAchievement, iconUrl: e.target.value })}
              />
              <div className="grid gap-2">
                <input
                  className="w-full rounded border border-gray-300 p-2 text-sm"
                  placeholder="Name color (#hex)"
                  value={newAchievement.nameColor ?? ""}
                  onChange={(e) => setNewAchievement({ ...newAchievement, nameColor: e.target.value })}
                />
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {resolveIconUrl(newAchievement.avatarFrame) ? (
                      <img
                        src={resolveIconUrl(newAchievement.avatarFrame)}
                        alt="Yeni avatar çerçevesi"
                        className="h-12 w-12 rounded-full border border-gray-200 object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-gray-300 text-[11px] text-gray-400">
                        Çerçeve yok
                      </div>
                    )}
                    <div className="flex gap-2">
                      <label className="cursor-pointer rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700">
                        Çerçeve Yükle
                        <input type="file" accept="image/*" className="hidden" onChange={handleNewAchievementFrameChange} />
                      </label>
                      {newAchievement.avatarFrame ? (
                        <button
                          type="button"
                          className="rounded bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                          onClick={clearNewAchievementFrame}
                        >
                          Kaldır
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <input
                    className="w-full rounded border border-gray-300 p-2 text-sm"
                    placeholder="Avatar frame URL"
                    value={newAchievement.avatarFrame ?? ""}
                    onChange={(e) => setNewAchievement({ ...newAchievement, avatarFrame: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <input
              className="sm:col-span-2 rounded border border-gray-300 p-2 text-sm"
              placeholder="Title"
              value={newAchievement.title}
              onChange={(e) => setNewAchievement({ ...newAchievement, title: e.target.value })}
            />
            <input
              className="sm:col-span-2 rounded border border-gray-300 p-2 text-sm"
              placeholder="Description"
              value={newAchievement.description}
              onChange={(e) => setNewAchievement({ ...newAchievement, description: e.target.value })}
            />
            <select
              className="rounded border border-gray-300 p-2 text-sm"
              value={newAchievement.conditionType}
              onChange={(e) => setNewAchievement({ ...newAchievement, conditionType: e.target.value })}
            >
              {conditionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="number"
              className="rounded border border-gray-300 p-2 text-sm"
              placeholder="Threshold"
              min={1}
              value={newAchievement.conditionValue}
              onChange={(e) => setNewAchievement({ ...newAchievement, conditionValue: Number(e.target.value) })}
            />
          </div>
          <div className="mt-3">
            <button
              onClick={handleCreateAchievement}
              className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Başarım oluştur
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}


